// features/profile/edit/ProfilePreview.tsx — the Edit Profile page's live preview (1.27.70).
// Built from the SAME ProfileBanner / ProfileBio / ProfileMeta the real profile
// renders, fed a User assembled from the unsaved draft, so what it shows is what
// other people will see after Save. It shows no made-up posts or counts.
import styled from 'styled-components';
import { Avatar } from '@/ui';
import { Icon } from '@/icons';
import { formatCount } from '@/components/formatCount';
import type { User } from '@/core/models';
import { ProfileBanner } from '../ProfileBanner';
import { ProfileBio } from '../ProfileBio';
import { ProfileMeta } from '../ProfileMeta';

const Root = styled.div<{ $large: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.bgRoot};
  overflow: hidden;
  > [role='img'] {
    height: ${({ $large }) => ($large ? '180px' : '110px')};
    border-radius: 0;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 16px 16px;
  min-width: 0;
`;

const AvatarRing = styled.div<{ $size: number }>`
  width: ${({ $size }) => $size + 8}px;
  margin-top: ${({ $size }) => -($size / 2)}px;
  padding: 4px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.bgRoot};
  position: relative;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
`;

const Name = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  line-height: ${({ theme }) => theme.lineHeight.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
  overflow-wrap: anywhere;
`;

const Supporter = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.color.supporter};
`;

const Handle = styled.p`
  margin: -8px 0 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Counts = styled.p`
  margin: 0;
  display: flex;
  gap: 18px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  > span > b {
    color: ${({ theme }) => theme.color.textPrimary};
    font-weight: 600;
    margin-right: 4px;
  }
`;

const Tabs = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  text-align: center;
  > span {
    padding: 10px 0;
  }
  > span:first-child {
    color: ${({ theme }) => theme.color.textPrimary};
    font-weight: 600;
    box-shadow: inset 0 -2px 0 ${({ theme }) => theme.color.accentCyan};
  }
`;

const Private = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.color.textMuted};
`;

export interface ProfilePreviewProps {
  user: User;
  /** The bigger version shown in the Preview profile dialog. */
  large?: boolean;
}

export function ProfilePreview({ user, large = false }: ProfilePreviewProps) {
  const avatarSize = large ? 96 : 72;
  const followers = user.stats?.followers ?? user.followerCount ?? 0;
  const following = user.stats?.following ?? 0;
  return (
    <Root $large={large} data-testid="profile-preview">
      <ProfileBanner user={user} />
      <Body>
        <AvatarRing $size={avatarSize}>
          <Avatar name={user.name || '?'} avatarKey={user.avatarKey} size={avatarSize} />
        </AvatarRing>
        <div>
          <NameRow>
            <Name>{user.name || 'Your name'}</Name>
            {user.verified && <Icon name="verify" size={18} title="Verified" />}
            {user.supporterBadge && (
              <Supporter>
                <Icon name="supporter-badge" size={16} title="Supporter" />
              </Supporter>
            )}
            {user.privateAccount && (
              <Private>
                <Icon name="lock" size={14} title="Private account" />
              </Private>
            )}
          </NameRow>
        </div>
        <Handle>{user.handle}</Handle>
        <ProfileBio user={user} />
        <ProfileMeta user={{ ...user, invitedBy: null }} />
        <Counts>
          <span>
            <b>{formatCount(following)}</b>Following
          </span>
          <span>
            <b>{formatCount(followers)}</b>
            {followers === 1 ? 'Follower' : 'Followers'}
          </span>
        </Counts>
        <Tabs aria-hidden="true">
          <span>Posts</span>
          <span>Media</span>
          <span>Cherished</span>
        </Tabs>
      </Body>
    </Root>
  );
}
